import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Modal from '@amzn/awsui-components-console/modal';
import { FlashbarProps } from '@amzn/awsui-components-console/flashbar';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import { UpdateTaskExecutionRequest } from 'aws-sdk/clients/datasync';
import React, { useState } from 'react';
import ErrorLoader from 'src/components/commonComponents/errorLoader/errorLoader';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { TaskStatus } from 'src/constants/commonEnums';
import HelpTextHOC from 'src/hocs/helpTextHOC';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import { logger } from 'src/utils/logger';
import { v4 as uuidv4 } from 'uuid';
import SubTaskExecutionView from 'src/components/selfServeWorkflows/SubTaskExecution/SubTaskExecutionView';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import { useAuthContext } from 'src/providers/AuthContext';
import { AUTHORISE_ERROR } from 'src/authentication/bindle-utils';
import { FormField, Textarea } from '@amzn/awsui-components-console';
import { useTaskOutput } from 'src/hooks/useTaskOutput';

export interface ActionButtonsProps {
	taskDetails;
	workflowId;
	workflowExecutionId;
	refreshView?: () => void;
	disabled?: boolean;
	showRefreshButton?: boolean | false;
	hasExecutePermissions?: boolean;
	authError?: string | null;
	setFlashBarItems?: (items: FlashbarProps.MessageDefinition[]) => void;
}

export default function TaskActionButtons({
	taskDetails,
	workflowId,
	workflowExecutionId,
	refreshView,
	disabled,
	showRefreshButton,
	hasExecutePermissions,
	authError,
	setFlashBarItems,
}: ActionButtonsProps) {
	const authContext = useAuthContext();
	const userName = authContext.userName;

	const derivedPath = taskDetails?.taskId
		? `/workflowExecutions/${workflowExecutionId}/tasks/${taskDetails.taskId}`
		: '';

	const { onClickCB: retryCB, result: retryResult } =
		useFetchOnUserActionUsingSDS<void>();

	const { isLoading: isRetryResultLoading, error: retryError } = retryResult;

	const { onClickCB: overrideCB, result: overrideResult } =
		useFetchOnUserActionUsingSDS<UpdateTaskExecutionRequest>();

	const { isLoading: isOverrideResultLoading, error: overrideError } =
		overrideResult;

	const {
		taskOutput,
		isLoadingTaskOutput,
		fetchTaskOutputData,
		resetTaskOutput,
	} = useTaskOutput({ workflowId, workflowExecutionId });

	const [taskStatus, setTaskStatus] = useState<TaskStatus>(
		TaskStatus.COMPLETED
	);

	const handleTaskStatusChange = async (
		onClickEvent: Event,
		taskStatus: TaskStatus
	) => {
		if (!comment.trim()) {
			setCommentValidation('Please provide justification');

			return;
		}

		onClickEvent.preventDefault();
		const requestId = uuidv4();
		let statusChangeResponse;

		if (TaskStatus.RE_RUN === taskStatus) {
			statusChangeResponse = await retryCB({
				path: `${derivedPath}/retry`,
				method: 'PATCH',
				headers: { requestId },
				body: {
					lastStatusUpdatedBy: userName,
					comment,
				},
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
			});
		} else {
			statusChangeResponse = await overrideCB({
				path: derivedPath,
				method: 'PUT',
				body: {
					requestId,
					status: taskStatus,
					lastStatusUpdatedBy: userName,
					comment,
				},
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
			});
		}

		logger.info(`Response of retry task is ${statusChangeResponse}`);
		setShowConfirmation(false);
		setTaskStatus(TaskStatus.COMPLETED);
		if (!statusChangeResponse.error && refreshView) {
			if (setFlashBarItems) {
				setFlashBarItems([
					{
						id: taskDetails.taskName,
						type: 'success',
						header: (
							<>
								Successfully updated the status for the task{' '}
								<strong>{taskDetails.taskName}</strong>.
							</>
						),
						dismissible: true,
						onDismiss: () => setFlashBarItems([]),
					},
				]);
				refreshView();
			}
		} else {
			const { flashBarItems: retryError } = retryResult;
			const { flashBarItems: overrideError } = overrideResult;

			if (setFlashBarItems) {
				setFlashBarItems([...retryError, ...overrideError]);
			}
		}
	};

	const [visible, setVisible] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [comment, setComment] = useState('');
	const [commentValidation, setCommentValidation] = useState('');

	const [isRefreshing, setIsRefreshing] = useState(false);

	const handleGetSubTaskList = (onClickEvent: Event) => {
		onClickEvent.preventDefault();
		setVisible(true);
		fetchTaskOutputData(taskDetails);
	};

	const handleConfirmationModal = (
		onClickEvent: Event,
		taskStatus: TaskStatus
	) => {
		onClickEvent.preventDefault();
		setShowConfirmation(true);
		setTaskStatus(taskStatus);
	};

	const getModalConfirmationHeader = () => {
		return TaskStatus.RE_RUN === taskStatus ? 'Retry Task' : 'Override Task';
	};

	const getConfirmationModalContent = () => {
		return (
			<>
				You are about to{' '}
				{TaskStatus.RE_RUN === taskStatus ? 'retry' : 'override'}{' '}
				<strong>{taskDetails.taskName}</strong> task. Are you sure?
				<FormField errorText={comment.trim() ? '' : commentValidation}>
					<Textarea
						onChange={({ detail }) => setComment(detail.value)}
						value={comment}
						placeholder="Enter comment"
					/>
				</FormField>
			</>
		);
	};

	const getAuthError = () => {
		return authError ? AUTHORISE_ERROR : null;
	};

	return (
		<>
			<div>
				<Modal
					size="large"
					onDismiss={() => {
						setVisible(false);
						resetTaskOutput();
					}}
					visible={visible}
					header={`${taskDetails.taskName} Details`}
					data-testid="task-actions-sub-tasks-modal"
				>
					{
						visible && (
							<SubTaskExecutionView
								taskDetails={taskDetails}
								workflowId={workflowId}
								workflowExecutionId={workflowExecutionId}
								taskOutput={taskOutput}
								isLoadingTaskOutput={isLoadingTaskOutput}
							/>
						)
					}
				</Modal>
				<Modal
					size="medium"
					onDismiss={() => setShowConfirmation(false)}
					visible={showConfirmation}
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="l">
								<Button
									onClick={() => setShowConfirmation(false)}
									variant="link"
								>
									Cancel
								</Button>
								<Button
									loading={isRetryResultLoading || isOverrideResultLoading}
									variant="primary"
									onClick={(e) => {
										handleTaskStatusChange(e, taskStatus);
									}}
									data-testid="task-actions-confirm-button"
								>
									Yes
								</Button>
							</SpaceBetween>
						</Box>
					}
					header={getModalConfirmationHeader()}
					data-testid="task-actions-confirmation-modal"
				>
					{getConfirmationModalContent()}
				</Modal>
				<ErrorLoader
					isLoading={false}
					error={retryError || overrideError || getAuthError()}
					isAlert
				/>
				<SpaceBetween direction="horizontal" size="s">
					{taskDetails.status === TaskStatus.FAILED ? (
						<SpaceBetween direction="horizontal" size="s">
							<HelpTextHOC
								helpText={
									hasExecutePermissions
										? 'Retry the task'
										: `${userName} does not have permissions to retry`
								}
								position="bottom"
							>
								<Button
									iconName="refresh"
									disabled={
										!hasExecutePermissions ||
										isOverrideResultLoading ||
										!derivedPath
									}
									onClick={(e) => {
										handleConfirmationModal(e, TaskStatus.RE_RUN);
									}}
									data-testid="retry-button"
								>
									Retry
								</Button>
							</HelpTextHOC>
							<HelpTextHOC
								helpText={
									hasExecutePermissions
										? 'Override the task'
										: `${userName} does not have permissions to override`
								}
								position="bottom"
							>
								<Button
									loading={isOverrideResultLoading}
									disabled={
										!hasExecutePermissions ||
										isRetryResultLoading ||
										!derivedPath
									}
									iconName="status-stopped"
									onClick={(e) =>
										handleConfirmationModal(e, TaskStatus.OVERRIDEN)
									}
									data-testid="override-button"
								>
									Override
								</Button>
							</HelpTextHOC>
						</SpaceBetween>
					) : (
						<></>
					)}
					<Button
						iconName="search"
						disabled={disabled}
						onClick={(e) => handleGetSubTaskList(e)}
						data-testid="view-sub-task-details-button"
					>
						{taskDetails.taskType === SUB_WORKFLOW_TASK_TYPE
							? 'View Sub-Workflow Execution'
							: 'View Sub Tasks'}
					</Button>
					{
						showRefreshButton &&
					<Button
						iconName="refresh"
						variant="icon"
						loading={isRefreshing}
						onClick={async () => {
							setIsRefreshing(true);
							try {
								if (refreshView) await refreshView();
							} finally {
								setIsRefreshing(false);
							}
						}}
						data-testid="refresh-tasks-button"
					/>
}
				</SpaceBetween>
			</div>
		</>
	);
}
